<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';
import ResizeObserver from 'resize-observer-polyfill';

import { clamp, convertToUnit } from '@components/utils';

import {
  CdsTransition,
  makeDisabledProps,
  makeFieldProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeLoadingProps,
  makeReadonlyProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useField,
  useFilteredInputAttrs,
  useFilteredInputProps,
  useFocus,
  useIcon,
  useInput,
  useLoading,
  useProxyModel
} from '../../composable';
import { CdsIcon } from '../CdsIcon';
import { CdsInput, CdsInputDescription, CdsInputLabel, CdsInputCounter } from '../CdsInput';
import { CdsLoader } from '../CdsLoader';
import { CdsFunctionalButton } from '../CdsButton';

import type { PropType } from 'vue';

export const componentName = 'CdsTextArea';
export default defineComponent({
  name: componentName,
  components: {
    CdsInput,
    CdsInputLabel,
    CdsInputDescription,
    CdsInputCounter,
    CdsIcon,
    CdsLoader,
    CdsFunctionalButton,
    CdsTransition
  },
  inheritAttrs: false,
  props: {
    ...makeFieldProps({
      clearLabel: 'Очистить текстовую область'
    }),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLoadingProps(),
    ...makeThemeProps(),

    /**
     * Автоматическое увеличение высоты текстовой области
     */
    autoGrow: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    /**
     * Количество строк в текстовой области
     */
    rows: {
      type: [Number, String] as PropType<number | string>,
      default: 5,
      validator: (value: any) => !isNaN(parseFloat(value))
    },
    /**
     * Максимальное количество строк в текстовой области
     */
    maxRows: {
      type: [Number, String] as PropType<number | string>,
      default: 0,
      validator: (value: any) => !isNaN(parseFloat(value))
    },
    /**
     * Отключение возможности изменения размера текстовой области
     */
    noResize: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { string }
     */
    'update:modelValue': (value: string) => true,
    /**
     * Событие очистки поля ввода
     */
    'click:clear': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке перед значением поля
     */
    'click:prepend': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке после значения поля
     */
    'click:append': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента перед значением поля
     */
    'click:prependInner': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента после значения поля
     */
    'click:appendInner': (event: MouseEvent) => true
  },
  setup(props, { slots, attrs, emit, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { id, hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { hasPrepend, hasAppend, iconClasses } = useIcon(props, slots, componentName);
    const { hasLoading, loadingClasses } = useLoading(props);
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const {
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      fieldClasses,
      fieldProps,
      handleClickPrependInner,
      handleClickAppendInner
    } = useField(props, slots, componentName);
    const { inputProps } = useFilteredInputProps(props, [
      ...fieldProps,
      'modelValue',
      'focused',
      'loading',
      'rows',
      'maxRows'
    ]);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);
    const hasClear = computed(() => props.clearable && !props.loading);
    const counterValue = computed(() => (model.value ?? '').toString().length);
    const disabledResize = computed(() => props.noResize || props.autoGrow);
    const hideMessagesAndCounter = computed(() => props.hideMessages && !props.counter && !props.limit);

    const fieldRef = ref<HTMLElement>();
    const textareaRef = ref<HTMLTextAreaElement>();
    const validationRef = ref<typeof CdsInput>();
    const sizerRef = ref<HTMLTextAreaElement>();

    const textareaHeight = ref('');

    const textareaClasses = computed(() => ({
      'cds-text-area--has-prefix': !!props.prefix,
      'cds-text-area--has-suffix': !!props.suffix,
      'cds-text-area--has-clear': unref(hasClear),
      'cds-text-area--disabled-resize': unref(disabledResize),
      'cds-text-area--auto-grow': props.autoGrow
    }));

    watch(
      () => props.value,
      (value) => {
        if (value) {
          model.value = value;
        }
      },
      { immediate: true }
    );

    watch(model, calculateTextareaHeight);
    watch(() => props.rows, calculateTextareaHeight);
    watch(() => props.maxRows, calculateTextareaHeight);

    onMounted(calculateTextareaHeight);

    const onInput = (event: Event) => {
      model.value = (event.target as HTMLTextAreaElement).value;
    };

    const onFocus = () => {
      if (unref(textareaRef) !== document.activeElement) {
        unref(textareaRef)?.focus();
      }

      if (!unref(isFocused)) focus();
    };

    const onClear = (event: MouseEvent) => {
      event.stopPropagation();

      onFocus();

      nextTick(() => {
        model.value = null;

        emit('click:clear', event);
      });
    };

    function calculateTextareaHeight() {
      if (!props.autoGrow) return;

      nextTick(() => {
        if (!unref(sizerRef) || !unref(textareaRef)) return;

        const style = getComputedStyle(unref(sizerRef)!);

        const height = unref(sizerRef)?.scrollHeight;
        const lineHeight = parseFloat(style.lineHeight);
        const minHeight = Math.max(parseFloat(props.rows as string) * lineHeight);
        const maxHeight = parseFloat(props.maxRows! as string) * lineHeight || Infinity;

        textareaHeight.value = convertToUnit(clamp(height ?? 0, minHeight, maxHeight));
      });
    }

    let observer: ResizeObserver | undefined;
    watch(sizerRef, (value) => {
      if (value) {
        observer = new ResizeObserver(calculateTextareaHeight);
        observer.observe(unref(sizerRef)!);
      } else {
        observer?.disconnect();
      }
    });

    onBeforeUnmount(() => {
      observer?.disconnect();
    });

    expose({
      id,
      isFocused,
      fieldRef,
      textareaRef,
      validationRef,
      sizerRef,
      focus: onFocus,
      blur
    });

    return {
      id,
      model,
      fieldRef,
      textareaRef,
      validationRef,
      sizerRef,
      isFocused,
      rootAttrs,
      inputAttrs,
      inputProps,
      hasLabel,
      hasDescription,
      hasPrepend,
      hasAppend,
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      hasLoading,
      hasClear,
      hideMessagesAndCounter,
      counterValue,
      iconClasses,
      textareaClasses,
      fieldClasses,
      loadingClasses,
      textareaHeight,
      themeClasses,
      onBlur: blur,
      onFocus,
      onInput,
      onClear,
      handleClickPrependInner,
      handleClickAppendInner
    };
  }
});
</script>

<template>
  <div ref="fieldRef" class="cds-text-area-field" :class="themeClasses" v-bind="rootAttrs">
    <cds-input-label v-if="hasLabel" :for="id" :text="label" :disabled="disabled">
      <!--
        @slot Лейбл
        @type string | html
      -->
      <slot name="label" />
    </cds-input-label>
    <cds-input-description v-if="hasDescription" :text="description">
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </cds-input-description>
    <cds-input
      :id="id"
      ref="validationRef"
      v-model="model"
      :focused="isFocused"
      class="cds-text-area"
      role="textbox"
      :class="[textareaClasses, fieldClasses, iconClasses, loadingClasses]"
      :style="{ '--cds-textarea-height': textareaHeight }"
      v-bind="{ ...inputProps, hideMessages: hideMessagesAndCounter }"
    >
      <template v-if="hasPrepend" #prepend>
        <!--
          @slot Элемент перед значением поля
          @type string | html
        -->
        <slot name="prepend" />
      </template>
      <template #default="{ isDisabled, isReadonly, isValid }">
        <div v-if="hasPrependInnerIcon" class="cds-text-area__prepend-inner" @click="handleClickPrependInner">
          <!--
            @slot Элемент внутри перед значением поля
            @type string | html
          -->
          <slot name="prepend-inner" />
          <cds-icon v-if="!!prependInnerIcon" :name="prependInnerIcon" />
        </div>
        <span v-if="prefix" class="cds-text-area__prefix" @click="onFocus">{{ prefix }}</span>
        <textarea
          :id="id"
          ref="textareaRef"
          :value="model"
          :placeholder="placeholder"
          :disabled="isDisabled.value"
          :readonly="isReadonly.value || loading"
          :aria-disabled="isReadonly.value || isDisabled.value"
          :aria-invalid="error || isValid.value === false"
          :autofocus="autofocus"
          :maxlength="limit ? +limit : undefined"
          :rows="rows"
          v-bind="{ ...inputAttrs }"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
        />
        <textarea
          v-if="autoGrow"
          ref="sizerRef"
          v-model="model"
          class="cds-text-area__sizer"
          readonly
          aria-hidden="true"
        />
        <div v-if="hasClear" class="cds-text-area__clear">
          <cds-functional-button
            :disabled="isDisabled.value"
            :readonly="isReadonly.value"
            :title="clearLabel"
            icon="remove"
            class="cds-text-area__clear--button"
            :class="{ active: !!model }"
            @click="onClear"
            @mousedown.prevent.stop
          />
        </div>
        <span v-if="suffix" class="cds-text-area__suffix" @click="onFocus">{{ suffix }}</span>
        <div v-if="hasAppendInnerIcon" class="cds-text-area__append-inner" @click="handleClickAppendInner">
          <!--
            @slot Элемент внутри после значения поля
            @type string | html
          -->
          <slot name="append-inner" />
          <cds-icon v-if="!!appendInnerIcon" :name="appendInnerIcon" />
        </div>
        <cds-transition transition="fade-transition" role="alert" aria-busy="true">
          <cds-loader v-if="hasLoading" size="xs" class="cds-text-area__loader" />
        </cds-transition>
      </template>
      <template v-if="hasAppend" #append>
        <!--
          @slot Элемент после значения поля
          @type string | html
        -->
        <slot name="append" />
      </template>
      <template #messages>
        <!--
          @slot Сообщения и подсказки
          @type string | html
        -->
        <slot name="messages" />
      </template>
    </cds-input>
    <cds-input-counter v-if="counter || limit" :current="counterValue" :max="limit" class="cds-text-area__counter" />
  </div>
</template>

<style lang="scss">
@import './CdsTextArea';
</style>
